import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { api, EVENT_LABEL } from "../../lib/api";
import { fmtDateLong, fmtTime, fmtDateTime } from "../../lib/format";
import { useAuth } from "../../auth";
import { can } from "../../lib/permissions";
import { Spinner, EmptyState } from "../../components/ui";
import { Icon } from "../../components/icons";

const STATUS_LABEL = {
  present: "Presente",
  pending: "Por responder",
  excused: "Dispensado",
  justified: "Falta justificada",
  unjustified: "Falta injustificada",
  expected: "Por confirmar",
};

function pillClass(status) {
  if (status === "present") return "done";
  if (status === "pending" || status === "expected") return "todo";
  return "doing";
}

const CLASSIFY = [
  { status: "present", icon: "checkmark", color: "var(--success)", label: "Presente" },
  { status: "justified", icon: "document-text", color: "var(--info)", label: "Falta justificada" },
  { status: "unjustified", icon: "close", color: "var(--warning)", label: "Falta injustificada" },
  { status: "excused", icon: "circle", color: "var(--muted)", label: "Dispensado" },
];

function Row({ eventId, m }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canClassify = can(user, "attendance.classify");
  const uid = m.user?.id || m.user_id;
  const set = useMutation({
    mutationFn: ({ status, note }) => api(`/admin/events/${eventId}/attendance/${uid}`, { method: "PUT", body: JSON.stringify({ status, note }) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["att-detail", eventId] }); qc.invalidateQueries({ queryKey: ["att-audit", eventId] }); },
    onError: (e) => window.alert(e.message),
  });
  const classify = (c) => {
    if (c.status === m.status) return;
    const note = c.status === "present" ? "" : window.prompt(`${c.label} — nota administrativa (opcional):`, "");
    if (note === null) return;
    set.mutate({ status: c.status, note: note || "" });
  };
  const declaredAt = m.declared_at || m.requested_at;
  const declaredReason = m.declared_reason || m.reason;
  const declared = m.declared_status ? m.declared_status !== "attending" : !!(m.requested_at || m.unavailable || m.reason);
  const attending = m.declared_status === "attending";
  return (
    <div className="card" style={{ display: "flex", alignItems: "center", gap: 12, padding: 12 }} data-testid={`att-member-${uid}`}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "var(--f-medium)", fontSize: 14 }}>{m.user?.name || "—"}</div>
        <div className="t-hint">{m.user?.voice || "Sem voz"}</div>
        <div className="t-hint" style={{ fontSize: 11 }} data-testid={`att-declaration-${uid}`}>
          Declaração do cantor: {declared ? `ausência${declaredAt ? ` em ${fmtDateTime(declaredAt)}` : ""}${declaredReason ? ` — ${declaredReason}` : ""}` : attending ? "confirmou presença" : "sem declaração"}
        </div>
        {m.validated_at ? (
          <div className="t-hint" style={{ fontSize: 11 }} data-testid={`att-validation-${uid}`}>
            Validado{m.validated_by_name ? ` por ${m.validated_by_name}` : ""} em {fmtDateTime(m.validated_at)}{m.note ? ` · Nota: ${m.note}` : ""}
          </div>
        ) : null}
      </div>
      <span className={`pill ${pillClass(m.status)}`} title="Classificação administrativa" data-testid={`att-status-${uid}`}>{(STATUS_LABEL[m.status] || m.status || "").toUpperCase()}</span>
      {canClassify ? (
        <div style={{ display: "flex", gap: 4 }} role="group" aria-label="Classificar presença">
          {CLASSIFY.map((c) => (
            <button key={c.status} className="icon-btn" title={c.label} aria-label={c.label} aria-pressed={m.status === c.status} onClick={() => classify(c)} data-testid={`att-set-${c.status}-${uid}`}
              style={{ width: 32, height: 32, ...(m.status === c.status ? { background: "var(--surface-3, var(--brand-tertiary))" } : {}) }}>
              <Icon name={c.icon} size={14} color={c.color} />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function AdminAttendanceDetail() {
  const { user } = useAuth();
  const { eventId } = useParams();
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ["att-detail", eventId], queryFn: () => api(`/admin/events/${eventId}/attendance`) });
  const data = q.data || {};
  const ev = data.event || {};
  const members = data.members || [];

  const convoked = members.filter((m) => m.convoked);
  const present = convoked.filter((m) => m.status === "present");
  const pending = convoked.filter((m) => m.status === "pending");
  const absent = convoked.filter((m) => ["excused", "justified", "unjustified"].includes(m.status));
  const unavailable = members.filter((m) => !m.convoked);

  return (
    <>
      <div className="page-header" style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button className="icon-btn" aria-label="Voltar" onClick={() => navigate(-1)}><Icon name="chevron-back" size={22} /></button>
        <div>
          <h1 className="t-h1" style={{ fontSize: 22 }}>{ev.title || "Assiduidade"}</h1>
          {ev.starts_at ? <div className="t-hint">{EVENT_LABEL[ev.type] || "Evento"} · {fmtDateLong(ev.starts_at)} · {fmtTime(ev.starts_at)}</div> : null}
        </div>
      </div>
      {q.isLoading ? <Spinner /> : (
        <div className="page ulc-fade" style={{ maxWidth: 820 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 18 }} data-testid="attendance-summary">
            <Stat n={present.length} label="Confirmaram presença" color="var(--success)" testid="stat-present" />
            <Stat n={pending.length} label="Falta responder" color="var(--warning)" testid="stat-pending" />
            <Stat n={absent.length} label="Ausências" color="var(--brand-primary)" testid="stat-absent" />
          </div>

          <Section title={`POR RESPONDER · ${pending.length}`} testid="attendance-pending-section">
            {pending.length ? pending.map((m) => <Row key={m.user?.id || m.user_id} eventId={eventId} m={m} />) : <div className="t-hint">Todos os convocados já responderam.</div>}
          </Section>

          <Section title={`PRESENÇAS CONFIRMADAS · ${present.length}`} testid="attendance-present-section">
            {present.length ? present.map((m) => <Row key={m.user?.id || m.user_id} eventId={eventId} m={m} />) : <div className="t-hint">Ainda sem presenças confirmadas.</div>}
          </Section>

          {absent.length ? (
            <Section title={`AUSÊNCIAS · ${absent.length}`} testid="attendance-absent-section">
              {absent.map((m) => <Row key={m.user?.id || m.user_id} eventId={eventId} m={m} />)}
            </Section>
          ) : null}

          {unavailable.length ? (
            <Section title={`INDISPONÍVEIS · ${unavailable.length}`} testid="attendance-unavailable-section">
              {unavailable.map((m) => <Row key={m.user?.id || m.user_id} eventId={eventId} m={m} />)}
            </Section>
          ) : null}

          {!members.length ? <EmptyState icon="people" text="Nenhum membro convocado para este evento." /> : null}

          {can(user, "attendance.audit.view") ? <AuditHistory eventId={eventId} /> : null}
        </div>
      )}
    </>
  );
}

// Append-only audit trail kept by the server; survives later corrections.
function AuditHistory({ eventId }) {
  const q = useQuery({ queryKey: ["att-audit", eventId], queryFn: () => api(`/admin/attendance/audit?event_id=${eventId}&limit=100`) });
  const rows = q.data || [];
  return (
    <Section title={`HISTÓRICO DE ALTERAÇÕES · ${rows.length}`} testid="attendance-audit-section">
      {q.isLoading ? <Spinner /> : null}
      {q.isError ? <div className="t-hint">{q.error.message}</div> : null}
      {q.isSuccess && !rows.length ? <div className="t-hint" data-testid="attendance-audit-empty">Ainda sem alterações registadas.</div> : null}
      {rows.map((a) => (
        <div key={a.id} className="card" style={{ padding: 10, fontSize: 13 }} data-testid={`attendance-audit-${a.id}`}>
          <div><strong>{a.member_name || a.user_id}</strong>: {(STATUS_LABEL[a.previous_status] || a.previous_status || "sem classificação")} → {STATUS_LABEL[a.new_status] || a.new_status}</div>
          <div className="t-hint" style={{ fontSize: 11 }}>{fmtDateTime(a.at)} · por {a.actor_name || a.actor_id}{a.note ? ` · ${a.note}` : ""}</div>
        </div>
      ))}
    </Section>
  );
}

function Stat({ n, label, color, testid }) {
  return (
    <div className="card" style={{ padding: 14, textAlign: "center" }} data-testid={testid}>
      <div style={{ fontFamily: "var(--f-medium)", fontSize: 30, color }}>{n}</div>
      <div className="t-hint" style={{ fontSize: 11 }}>{label}</div>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div className="t-section" style={{ marginBottom: 8 }} data-testid={testid}>{title}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{children}</div>
    </div>
  );
}
